import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Link } from '@material-ui/core';

import { CampaignType } from '@dev/ee-api/ee-api-models';
import { formatNumber, Tip, Typography, ContentLoader, Tooltip, InfoIcon } from '@dev/ee-components';

import { useCampaignScreensPathsChecker } from '@/components/CampaignCreator/hooks/useCampaignScreensPathsChecker';
import { isCustomBranding } from '@/services/Secuirty';
import { links } from '@/services/dictionaries/links';
import {
    getCampainCreatorStore,
    LoadCompletedCampaignsCountStart,
    SetAbRecipients,
} from '@/store/campaignCreator/actions';
import { TooltipIconWrapper } from '@/components/CampaignCreator/styled';

const SummaryWrapper = styled.div`
    margin-top: 32px;
`;

const SummaryDescriptionWrapper = styled.div`
    margin-top: 5px;
    display: flex;
    align-items: center;
`;

const TipWrapper = styled.div`
    margin-left: 12px;
`;

const TextWrapper = styled.div`
    display: flex;
    align-items: center;
`;

export const Summary = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        recipientsCount,
        isRecipientsCountLoading,
        campaign,
        isLoading,
        completedCampaignsCount,
        completedCampaignsLoading,
    } = useSelector(getCampainCreatorStore);
    const { isAbCampaign, isDetailsScreen } = useCampaignScreensPathsChecker();
    const isSummaryLoading = isDetailsScreen ? isLoading : isRecipientsCountLoading;
    const showTip = !completedCampaignsCount && !completedCampaignsLoading && !isDetailsScreen;
    const showTooltip = isAbCampaign && !isDetailsScreen;

    useEffect((): void => {
        if (completedCampaignsCount || isDetailsScreen) {
            return;
        }
        dispatch(LoadCompletedCampaignsCountStart());
    }, []);

    useEffect((): void => {
        if (!isAbCampaign) {
            return;
        }
        dispatch(SetAbRecipients());
    }, [!isSummaryLoading]);

    if (campaign?.Type === CampaignType.Automated) {
        return null;
    }

    return (
        <SummaryWrapper>
            <Typography variant="h2">{t('summary')}</Typography>
            <SummaryDescriptionWrapper>
                <Typography variant="h4">
                    {isSummaryLoading ? (
                        <ContentLoader mode="row" height={21} width={200} />
                    ) : (
                        <TextWrapper>
                            <div>
                                <Trans
                                    i18nKey={isDetailsScreen ? 'yourcampaignhasbeensentto' : 'yourcampaignwillbesentto'}
                                    components={{
                                        h2: <span style={{ fontSize: '18px' }} />,
                                    }}
                                    values={{
                                        count: formatNumber(
                                            isDetailsScreen ? campaign?.Stats?.Delivered : recipientsCount || 0
                                        ),
                                    }}
                                />
                            </div>
                            {showTooltip && (
                                <Tooltip id="ab-campaign-summary-tooltip" title={t('summaryabcampaigncreator_tooltip')}>
                                    <TooltipIconWrapper isAbCampaign>
                                        <InfoIcon />
                                    </TooltipIconWrapper>
                                </Tooltip>
                            )}
                        </TextWrapper>
                    )}
                </Typography>
                {showTip && (
                    <TipWrapper>
                        <Tip id="sendto-tip">
                            {isCustomBranding() ? (
                                t('sendtocampaigncreator_tip_cb')
                            ) : (
                                <Trans
                                    i18nKey="sendtocampaigncreator_tip_noncb"
                                    components={{
                                        url: <Link href={links.deliveryGuide} target="_blank" />,
                                    }}
                                />
                            )}
                        </Tip>
                    </TipWrapper>
                )}
            </SummaryDescriptionWrapper>
        </SummaryWrapper>
    );
};
